import React, { useEffect } from 'react';
import ModalPortal from "../../../components/common/ModalPortal";
import { Button } from '../../../components/common/Button';
import { Clock, CheckCircle } from 'lucide-react';

interface TimerCompletionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSeeFeedback: () => void;
}

/**
 * TimerCompletionModal - Modal shown when interview timer completes
 * 
 * This component is responsible for:
 * 1. Displaying a notification when the interview time is up
 * 2. Providing a "See Feedback" button to navigate to feedback
 * 3. Ensuring media tracks are preserved during modal display
 */
const TimerCompletionModal: React.FC<TimerCompletionModalProps> = ({ 
  isOpen, 
  onClose,
  onSeeFeedback
}) => {
  // Prevent modal from affecting camera stream
  useEffect(() => {
    if (isOpen) {
      // When modal opens, ensure all media tracks maintain their state
      const maintainMediaTracks = () => {
        const videoEl = document.querySelector('video');
        if (videoEl && videoEl.srcObject instanceof MediaStream) {
          const mediaStream = videoEl.srcObject;
          mediaStream.getTracks().forEach(track => {
            // Don't change the state, just ensure track properties are preserved
            console.log(`Preserving ${track.kind} track state: ${track.enabled ? 'enabled' : 'disabled'}`);
          });
        }
      };
      
      maintainMediaTracks();
    }
  }, [isOpen]);

  if (!isOpen) return null;
  
  return (
    <ModalPortal>
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in z-[100]">
        <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-xl max-w-md w-full border border-border-light dark:border-border-dark animate-fade-in-up">
          <div className="p-5 border-b border-border-light dark:border-border-dark">
            <div className="flex items-center">
              <div className="p-2 rounded-full bg-green-500/10 mr-3">
                <CheckCircle className="w-5 h-5 text-green-500" />
              </div>
              <h3 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark">
                Interview Complete
              </h3>
            </div>
          </div>
          <div className="p-5">
            <p className="text-base text-text-secondary-light dark:text-text-secondary-dark mb-4">
              Great job! Your interview time is up. Your session has been recorded and we've analyzed your performance. Click below to see your detailed feedback.
            </p>

            <div className="mt-6 flex justify-center">
              <Button 
                variant="primary" 
                onClick={onSeeFeedback}
                className="w-full sm:w-auto"
              >
                See Feedback
              </Button>
            </div>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
};

export default TimerCompletionModal; 